import { useCallback, useRef, useMemo, useState } from 'react';
import { Button, Space, Tag, Modal, message, Select, Alert, Upload, Tooltip } from 'antd';
import {
  DeleteOutlined,
  UploadOutlined,
  DownloadOutlined,
  EyeOutlined,
  LockOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getFiles,
  deleteFile,
  uploadFile,
  downloadFile,
  createFile,
  FILE_CATEGORIES,
  FILE_ENTITY_TYPES,
  type FileDocument,
  type FileFilters,
} from '@/api/fileApi';
import { useAuth } from '@/hooks/useAuth';
import FilesNav from './FilesNav';

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);
  return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export default function FileListPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [categoryFilter, setCategoryFilter] = useState<string | undefined>();
  const [entityTypeFilter, setEntityTypeFilter] = useState<string | undefined>();
  const [uploading, setUploading] = useState(false);

  const canRead = hasPermission('file', 'read');
  const canWrite = hasPermission('file', 'write');
  const canDelete = hasPermission('file', 'delete');

  const fetchData = useCallback(
    (params: SearchParams) => {
      const filters: FileFilters = { ...params };
      if (categoryFilter) filters.category = categoryFilter;
      if (entityTypeFilter) filters.entity_type = entityTypeFilter;
      return getFiles(filters);
    },
    [categoryFilter, entityTypeFilter]
  );

  const handleUpload = useCallback(
    async (file: File) => {
      setUploading(true);
      try {
        const result = await createFile({
          name: file.name,
          original_name: file.name,
          file_size: file.size,
          content_type: file.type || 'application/octet-stream',
        });
        if (result.data?.id) {
          await uploadFile(result.data.id, file);
        }
        message.success(t('file.upload_success'));
        gridRef.current?.reload();
      } catch {
        message.error(t('app.error'));
      } finally {
        setUploading(false);
      }
      return false;
    },
    [t]
  );

  const handleDownload = useCallback(
    async (record: FileDocument) => {
      try {
        const response = await downloadFile(record.id);
        const blob = response.data;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = record.original_name || record.name;
        link.click();
        URL.revokeObjectURL(url);
      } catch {
        message.error(t('app.error'));
      }
    },
    [t]
  );

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('file.confirm_delete'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteFile(id);
            message.success(t('file.delete_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const columns: ColumnsType<FileDocument> = useMemo(
    () => [
      {
        title: t('file.field.name'),
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
        sorter: true,
      },
      {
        title: t('file.field.file_size'),
        dataIndex: 'file_size',
        key: 'file_size',
        width: 100,
        render: (v: number) => formatFileSize(v),
      },
      {
        title: t('file.field.content_type'),
        dataIndex: 'content_type',
        key: 'content_type',
        width: 140,
        ellipsis: true,
      },
      {
        title: t('file.field.category'),
        dataIndex: 'category',
        key: 'category',
        width: 120,
        render: (v: string) =>
          v ? <Tag>{t(`file.category_options.${v}`, v)}</Tag> : '-',
      },
      {
        title: t('file.field.entity_type'),
        dataIndex: 'entity_type',
        key: 'entity_type',
        width: 120,
        render: (v: string) =>
          v ? <Tag color="blue">{t(`file.entity_type_options.${v}`, v)}</Tag> : '-',
      },
      {
        title: t('file.field.is_public'),
        dataIndex: 'is_public',
        key: 'is_public',
        width: 80,
        render: (v: boolean) =>
          v ? (
            <Tooltip title={t('file.field.is_public')}>
              <EyeOutlined style={{ color: '#52c41a' }} />
            </Tooltip>
          ) : (
            <Tooltip title={t('file.private')}>
              <LockOutlined style={{ color: '#999' }} />
            </Tooltip>
          ),
      },
      {
        title: t('file.field.download_count'),
        dataIndex: 'download_count',
        key: 'download_count',
        width: 80,
        sorter: true,
      },
      {
        title: t('file.field.tags'),
        dataIndex: 'tags',
        key: 'tags',
        width: 160,
        render: (tags: string[]) =>
          tags?.length > 0 ? (
            <Space size={2} wrap>
              {tags.slice(0, 3).map((tag) => (
                <Tag key={tag} color="geekblue">
                  {tag}
                </Tag>
              ))}
              {tags.length > 3 && <Tag>+{tags.length - 3}</Tag>}
            </Space>
          ) : (
            '-'
          ),
      },
      {
        title: t('file.field.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        width: 160,
        sorter: true,
        render: (v: string) => (v ? new Date(v).toLocaleString() : '-'),
      },
      {
        title: t('app.actions'),
        key: 'col_actions',
        width: 120,
        render: (_: unknown, record: FileDocument) => (
          <Space size="small">
            <Tooltip title={t('file.download')}>
              <Button
                type="link"
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => handleDownload(record)}
              />
            </Tooltip>
            {canDelete && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
              />
            )}
          </Space>
        ),
      },
    ],
    [t, canDelete, handleDelete, handleDownload]
  );

  const toolbar = (
    <Space wrap>
      <Select
        allowClear
        placeholder={t('file.field.category')}
        value={categoryFilter}
        onChange={(v) => {
          setCategoryFilter(v);
          gridRef.current?.reload();
        }}
        style={{ width: 140 }}
        options={FILE_CATEGORIES.map((c) => ({
          value: c,
          label: t(`file.category_options.${c}`),
        }))}
      />
      <Select
        allowClear
        placeholder={t('file.field.entity_type')}
        value={entityTypeFilter}
        onChange={(v) => {
          setEntityTypeFilter(v);
          gridRef.current?.reload();
        }}
        style={{ width: 140 }}
        options={FILE_ENTITY_TYPES.map((et) => ({
          value: et,
          label: t(`file.entity_type_options.${et}`),
        }))}
      />
      {canWrite && (
        <Upload
          showUploadList={false}
          beforeUpload={handleUpload}
          disabled={uploading}
        >
          <Button
            type="primary"
            icon={<UploadOutlined />}
            loading={uploading}
          >
            {t('file.upload')}
          </Button>
        </Upload>
      )}
    </Space>
  );

  if (!canRead) return <Alert type="error" message={t('app.forbidden')} />;

  return (
    <div>
      <FilesNav />
      <h2>{t('file.list_title')}</h2>
      <DataGrid<FileDocument>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('file.search_placeholder')}
        defaultSortField="created_at"
        defaultSortDescending
        toolbar={toolbar}
      />
    </div>
  );
}
